"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CarouselSlider = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
const React = __importStar(require("react"));
const classnames_1 = __importDefault(require("classnames"));
const CarouselContext_js_1 = require("./CarouselContext.js");
const index_js_1 = require("../utils/index.js");
/**
 * `CarouselSlider` is the scrollable list that should consist of `CarouselSlide` components.
 */
exports.CarouselSlider = React.forwardRef((props, ref) => {
    const { children, className, ...rest } = props;
    const context = React.useContext(CarouselContext_js_1.CarouselContext);
    if (!context) {
        throw new Error('CarouselSlider must be used within Carousel');
    }
    const { setSlideCount, idPrefix, scrollToSlide, isManuallyUpdating } = context;
    const items = React.useMemo(() => {
        var _a;
        return (_a = React.Children.map(children, (child, index) => React.isValidElement(child)
            ? React.cloneElement(child, {
                id: `${idPrefix}--slide-${index}`,
                index,
            })
            : child)) !== null && _a !== void 0 ? _a : [];
    }, [children, idPrefix]);
    (0, index_js_1.useIsomorphicLayoutEffect)(() => {
        setSlideCount(items.length);
    }, [items.length, setSlideCount]);
    const sliderRef = React.useRef(null);
    const refs = (0, index_js_1.useMergedRefs)(sliderRef, ref);
    scrollToSlide.current = (slideIndex, { instant } = {}) => {
        var _a, _b, _c;
        isManuallyUpdating.current = true; // start manual update
        const slideToShow = (_a = sliderRef.current) === null || _a === void 0 ? void 0 : _a.children.item(slideIndex);
        if (!sliderRef.current || !slideToShow) {
            return;
        }
        const motionOk = (_c = (_b = (0, index_js_1.getWindow)()) === null || _b === void 0 ? void 0 : _b.matchMedia('(prefers-reduced-motion: no-preference)')) === null || _c === void 0 ? void 0 : _c.matches;
        sliderRef.current.scrollTo({
            left: slideToShow.offsetLeft - sliderRef.current.offsetLeft,
            behavior: (instant || !motionOk ? 'instant' : 'smooth'), // scrollTo accepts 'instant' but ScrollBehavior type is wrong
        });
    };
    const scrollTimeout = React.useRef();
    // reset isManuallyUpdating.current to false after the last scroll event
    const handleOnScroll = React.useCallback(() => {
        var _a, _b;
        if (scrollTimeout.current) {
            (_a = (0, index_js_1.getWindow)()) === null || _a === void 0 ? void 0 : _a.clearTimeout(scrollTimeout.current);
        }
        scrollTimeout.current = (_b = (0, index_js_1.getWindow)()) === null || _b === void 0 ? void 0 : _b.setTimeout(() => {
            isManuallyUpdating.current = false;
        }, 100);
    }, [isManuallyUpdating]);
    return (React.createElement("ol", { "aria-live": 'polite', className: (0, classnames_1.default)('iui-carousel-slider', className), ref: refs, onScroll: handleOnScroll, ...rest }, items));
});
